import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import StatCard from "../components/StatCard";
import { useAuth } from "../Auth-Context/AuthContext";
import { getDashboardData } from "../api/services";
import { 
  TrendingUp, Briefcase, Clock, Users, 
  Phone, Mail, Calendar, FileText, 
  PieChart, BarChart3, Activity, Inbox 
} from "lucide-react";

/* ── Helper to format labels ── */
const formatLabel = (str) => str.replace(/_/g, " ");

/* ── Interaction Type Badge ── */
const INTERACTION_STYLES = {
  CALL: { bg: "#dcfce7", color: "#16a34a", icon: Phone, label: "Calls" },
  EMAIL: { bg: "#dbeafe", color: "#1d4ed8", icon: Mail, label: "Emails" },
  MEETING: { bg: "#ede9fe", color: "#7c3aed", icon: Calendar, label: "Meetings" },
  NOTE: { bg: "#fef3c7", color: "#d97706", icon: FileText, label: "Notes" },
};

/* ── Pipeline Value Chart Component ── */
function PipelineValueChart({ stages, totalValue }) {
  const hasData = stages && Object.keys(stages).length > 0 && totalValue > 0;
  
  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] gap-3">
        <Inbox size={32} className="text-neutral-300" />
        <div className="text-[13px] text-neutral-400">No pipeline data available</div>
        <div className="text-[11px] text-neutral-300">Add deals to see pipeline value</div>
      </div>
    );
  }

  const stageData = Object.entries(stages)
    .filter(([_, value]) => value > 0)
    .map(([stage, value]) => ({
      name: stage,
      value: value,
      percentage: (value / totalValue) * 100,
    }));

  const maxValue = Math.max(...stageData.map(d => d.value));

  return (
    <div className="h-[200px] flex items-end gap-2 pt-4">
      {stageData.map((stage, i) => {
        const height = (stage.value / maxValue) * 160;
        const colors = {
          LEAD: "#8B5CF6",
          CONTACTED: "#EC4899",
          PROPOSAL: "#F59E0B",
          WON: "#10B981",
          LOST: "#6B7280",
        };
        const color = colors[stage.name] || "#8B5CF6";
        return (
          <div key={i} className="flex flex-col items-center flex-1 gap-2">
            <div className="relative flex justify-center w-full group">
              <div 
                className="w-full max-w-[40px] rounded-t-lg transition-all duration-500 cursor-pointer hover:opacity-80"
                style={{ 
                  height: `${height}px`, 
                  background: color,
                }}
              />
              {/* Tooltip on hover */}
              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-neutral-800 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap">
                ${(stage.value / 1000).toFixed(1)}k
              </div>
            </div>
            <div className="text-[10px] text-neutral-500 truncate w-full text-center">
              {formatLabel(stage.name)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Status Distribution Component ── */
function StatusDistribution({ contactsByStatus }) {
  const hasData = contactsByStatus && Object.keys(contactsByStatus).length > 0;
  
  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-[120px] gap-2">
        <Inbox size={28} className="text-neutral-300" />
        <div className="text-[13px] text-neutral-400">No contact data available</div>
        <div className="text-[11px] text-neutral-300">Add Companies to see distribution</div>
      </div>
    );
  }

  const statusData = Object.entries(contactsByStatus).filter(([_, count]) => count > 0);
  const total = statusData.reduce((sum, [_, count]) => sum + count, 0);
  const colors = {
    PROSPECTING: "#8B5CF6",
    LEAD_QUALIFICATION: "#EC4899",
    DISCOVERY_PAIN_POINTS: "#F59E0B",
    DEMO_PITCH: "#10B981",
    PROPOSAL: "#3B82F6",
    NEGOTIATION: "#6366F1",
    CLOSED_WON: "#059669",
    CLOSED_LOST: "#EF4444",
    CUSTOMER: "#009DB9",
  };

  return (
    <div className="space-y-3">
      {statusData.map(([status, count]) => {
        const percentage = total > 0 ? (count / total) * 100 : 0;
        const color = colors[status] || "#8B5CF6";
        const label = formatLabel(status);
        return (
          <div key={status} className="flex flex-col gap-1 group">
            <div className="flex items-center justify-between text-[12px]">
              <span className="font-medium text-neutral-600">{label}</span>
              <div className="flex items-center gap-2">
                <span className="text-neutral-500">{count}</span>
                <span className="text-neutral-400 text-[10px]">{percentage.toFixed(0)}%</span>
              </div>
            </div>
            <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
              <div 
                className="h-full transition-all duration-500 rounded-full group-hover:opacity-80" 
                style={{ width: `${percentage}%`, background: color }} 
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Interactions Summary Component ── */
function InteractionsSummary({ interactionsByType }) {
  const hasData = interactionsByType && Object.keys(interactionsByType).length > 0;
  const total = hasData ? Object.values(interactionsByType).reduce((sum, count) => sum + count, 0) : 0;
  
  if (!hasData || total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[120px] gap-2">
        <Activity size={28} className="text-neutral-300" />
        <div className="text-[13px] text-neutral-400">No interactions yet</div>
        <div className="text-[11px] text-neutral-300">Log calls, emails, or meetings</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {Object.entries(interactionsByType).map(([type, count]) => {
        const style = INTERACTION_STYLES[type] || INTERACTION_STYLES.NOTE;
        const Icon = style.icon;
        const percentage = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={type} className="flex items-center gap-2 p-3 transition-colors bg-neutral-50 rounded-xl hover:bg-neutral-100">
            <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg"
              style={{ background: style.bg }}>
              <Icon size={14} style={{ color: style.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-neutral-500">{style.label}</div>
              <div className="text-[15px] font-bold text-neutral-900">{count}</div>
            </div>
            <div className="text-[11px] text-neutral-400 font-medium">{percentage.toFixed(0)}%</div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Deals by Stage Component ── */
function DealsByStage({ dealsByStage }) {
  const hasData = dealsByStage && Object.keys(dealsByStage).length > 0;
  const total = hasData ? Object.values(dealsByStage).reduce((sum, count) => sum + count, 0) : 0;
  
  if (!hasData || total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] gap-2">
        <BarChart3 size={32} className="text-neutral-300" />
        <div className="text-[13px] text-neutral-400">No deals in pipeline</div>
        <div className="text-[11px] text-neutral-300">Create deals to track progress</div>
      </div>
    );
  }

  const colors = {
    LEAD: "#8B5CF6",
    CONTACTED: "#EC4899",
    PROPOSAL: "#F59E0B",
    WON: "#10B981",
    LOST: "#6B7280",
  };

  return (
    <div className="space-y-3">
      {Object.entries(dealsByStage)
        .filter(([_, count]) => count > 0)
        .map(([stage, count]) => {
          const percentage = total > 0 ? (count / total) * 100 : 0;
          const color = colors[stage] || "#8B5CF6";
          const label = formatLabel(stage);
          return (
            <div key={stage} className="flex flex-col gap-1 group">
              <div className="flex items-center justify-between text-[12px]">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: color }} />
                  <span className="font-medium text-neutral-600">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-neutral-500">{count}</span>
                  <span className="text-neutral-400 text-[10px]">{percentage.toFixed(0)}%</span>
                </div>
              </div>
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div 
                  className="h-full transition-all duration-500 rounded-full group-hover:opacity-80" 
                  style={{ width: `${percentage}%`, background: color }} 
                />
              </div>
            </div>
          );
        })}
    </div>
  );
}

/* ── Main Dashboard ── */
export default function Dashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboardData();
      setDashboardData(data);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      setError(err.response?.data?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Extract data from API response
  const stats = {
    totalContacts: dashboardData?.totalContacts || 0,
    totalLeads: dashboardData?.totalLeads || 0,
    totalCustomers: dashboardData?.totalCustomers || 0,
    activeDeals: dashboardData?.activeDeals || 0,
    totalPipelineValue: dashboardData?.totalPipelineValue || 0,
    wonDealsValue: dashboardData?.wonDealsValue || 0,
    tasksToday: dashboardData?.tasksToday || 0,
    overdueTasksCount: dashboardData?.overdueTasksCount || 0,
  };

  const dealsByStage = dashboardData?.dealsByStage || {};
  const pipelineValueByStage = dashboardData?.pipelineValueByStage || {};
  const contactsByStatus = dashboardData?.contactsByStatus || {};
  const interactionsByType = dashboardData?.interactionsByType || {};
  const todaysTasks = dashboardData?.todaysTasks || [];

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-neutral-50">
        <Sidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <Topbar title="Dashboard" subtitle="Loading..." />
          <div className="flex items-center justify-center flex-1">
            <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-[#009DB9]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen overflow-hidden bg-neutral-50">
        <Sidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <Topbar title="Dashboard" subtitle="Error loading data" />
          <div className="flex flex-col items-center justify-center flex-1 gap-3">
            <div className="text-[14px] text-red-600 bg-red-50 px-4 py-2 rounded-lg">{error}</div>
            <button onClick={loadDashboardData} 
              className="px-4 py-2 text-[13px] font-semibold text-white rounded-lg bg-[#009DB9] hover:bg-[#007a90]">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-50">
      <Sidebar />

      <div className="flex flex-col flex-1 overflow-hidden">
        <Topbar
          title="Dashboard"
          subtitle={new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        />

        <main className="flex-1 py-5 overflow-y-auto px-7">

          {/* Welcome */}
          <div className="mb-4">
            <div className="text-[18px] font-bold text-neutral-900 tracking-[-0.4px]">Good morning 👋</div>
            <div className="text-[12px] text-neutral-400 mt-0.5">
              You have{" "}
              <strong className="font-semibold text-red-500">{stats.overdueTasksCount} overdue task{stats.overdueTasksCount !== 1 ? "s" : ""}</strong>
              {" "}and {stats.activeDeals} deals in the pipeline today.
            </div>
          </div>

          {/* Stat cards - Row 1 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 mb-4">
            <StatCard icon={TrendingUp} label="Total Revenue" value={`$${stats.wonDealsValue.toLocaleString()}`} trend={{ value: "closed deals", direction: "neutral" }} subtext="from won deals" />
            <StatCard icon={Briefcase} label="Active Deals" value={`${stats.activeDeals}`} trend={{ value: "in pipeline", direction: "up" }} subtext="open opportunities" />
            <StatCard icon={Clock} label="Tasks Today" value={`${stats.tasksToday}`} trend={{ value: `${stats.overdueTasksCount} overdue`, direction: stats.overdueTasksCount > 0 ? "down" : "neutral" }} subtext="action required" />
            <StatCard icon={Users} label="Total Companies" value={`${stats.totalContacts}`} trend={{ value: `${stats.totalLeads} leads · ${stats.totalCustomers} customers`, direction: "neutral" }} subtext="in database" />
          </div>

          {/* Pipeline Value Chart + Interactions Summary */}
          <div className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-2">
            {/* Pipeline Value Chart */}
            <div className="p-5 bg-white border rounded-xl border-neutral-100" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[14px] font-bold text-neutral-900">Pipeline Value</div>
                  <div className="text-[11px] text-neutral-400 mt-0.5">By stage</div>
                </div>
                {stats.totalPipelineValue > 0 && (
                  <div className="text-[20px] font-bold text-[#009DB9]">${(stats.totalPipelineValue / 1000).toFixed(1)}k</div>
                )}
              </div>
              <PipelineValueChart stages={pipelineValueByStage} totalValue={stats.totalPipelineValue} />
            </div>

            {/* Interactions Summary */}
            <div className="p-5 bg-white border rounded-xl border-neutral-100" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div className="flex items-center gap-2 mb-4">
                <Activity size={16} className="text-neutral-400" />
                <div className="text-[14px] font-bold text-neutral-900">Interactions</div>
              </div>
              <InteractionsSummary interactionsByType={interactionsByType} />
            </div>
          </div>

          {/* Deals by Stage + Contact Status */}
          <div className="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-2">
            {/* Deals by Stage */}
            <div className="p-5 bg-white border rounded-xl border-neutral-100" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={16} className="text-neutral-400" />
                <div className="text-[14px] font-bold text-neutral-900">Deals by Stage</div>
              </div>
              <DealsByStage dealsByStage={dealsByStage} />
            </div>

            {/* Contact Status Distribution */}
            <div className="p-5 bg-white border rounded-xl border-neutral-100" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div className="flex items-center gap-2 mb-4">
                <PieChart size={16} className="text-neutral-400" />
                <div className="text-[14px] font-bold text-neutral-900">Company Status</div>
              </div>
              <StatusDistribution contactsByStatus={contactsByStatus} />
            </div>
          </div>

          {/* Today's Tasks */}
          <div className="bg-white border rounded-xl border-neutral-100" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
              <span className="text-[13px] font-bold text-neutral-900">Today's Tasks</span>
              <button className="text-[12px] font-medium transition-colors" style={{ color: "#009DB9" }}
                onClick={() => navigate("/tasks")}>
                View all →
              </button>
            </div>
            <div className="px-4 divide-y divide-neutral-100">
              {todaysTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8">
                  <Inbox size={28} className="text-neutral-300" />
                  <div className="text-[12px] text-neutral-400">No tasks for today</div>
                  <div className="text-[11px] text-neutral-300">All caught up!</div>
                </div>
              ) : (
                todaysTasks.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 py-3">
                    <input type="checkbox" className="mt-0.5 w-3.5 h-3.5 rounded accent-[#009DB9] flex-shrink-0 cursor-pointer" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-neutral-900">{item.task || item.title}</div>
                      <div className="text-[11px] text-neutral-400 mt-0.5 truncate">{item.contact}</div>
                    </div>
                    <span className={`text-[11px] font-semibold flex-shrink-0 ${
                      item.status === "Overdue" ? "text-red-500" : 
                      item.status === "Today" ? "text-amber-500" : 
                      "text-green-500"
                    }`}>
                      {item.status || "Pending"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

        </main>
      </div>
    </div>
  );
}